import React, { useState } from "react";

import { Modal } from "reactstrap";
import { toast } from "react-toastify";

import "assets/css/home-modern.css";
import "assets/css/flow.css";
import "assets/css/admin.css";

// core components
import DefaultFooter from "components/Footers/DefaultFooter.js";
import IndexNavbar from "components/Navbars/IndexNavbar";
import MetaList from "components/Admin/MetaList";
import PassCollection from "components/Admin/PassCollection";
import RowMenu from "components/Admin/RowMenu";
import TableState from "components/Admin/TableState";
import { APIRequest } from "utils/ApiRequest";
import { formatINR } from "utils/format";
import { useNavigate } from "react-router";

const TAB_PASSES = "1";
const TAB_PENDING = "2";
const TAB_SUCCESS = "3";
const TAB_FIND_ORDER = "4";
const TAB_ADD_PRODUCT = "6";
const TAB_PRODUCTS = "7";
const TAB_CORRECT_ORDER = "9";

// Grouped for the desktop sidebar. On mobile the group labels are hidden by
// CSS and this flattens back into the single scrolling pill rail.
const TAB_GROUPS = [
  {
    label: "Passes",
    tabs: [{ id: TAB_PASSES, label: "Passes" }],
  },
  {
    label: "Orders",
    tabs: [
      { id: TAB_PENDING, label: "Pending orders" },
      { id: TAB_SUCCESS, label: "Successful orders" },
      // The cross-year lookup by mobile or email. It used to share the Passes tab, but it answers
      // a different question — find any contribution — and the pass queue has its own search now.
      { id: TAB_FIND_ORDER, label: "Find a contribution" },
      { id: TAB_CORRECT_ORDER, label: "Correct order" },
    ],
  },
  {
    label: "Catalogue",
    tabs: [
      { id: TAB_PRODUCTS, label: "All products" },
      { id: TAB_ADD_PRODUCT, label: "Add product" },
    ],
  },
];

const TS_CURSOR_START = 1000000000000000000;

const RAZORPAY_DASHBOARD_URL = "https://dashboard.razorpay.com/app/payments";

const TYPE_TONES = {
  MEMBERSHIP: "hm-tag-indigo",
  SPONSORSHIP: "hm-tag-gold",
  DAILY_PASS: "hm-tag-green",
};

function StatusTag({ status }) {
  if (status === undefined || status === null || status === "") return <span className="hm-tag hm-tag-mute">—</span>;
  const upper = String(status).toUpperCase();
  let tone = "hm-tag-mute";
  if (upper.includes("SUCCESS") || upper.includes("APPROVE") || upper.includes("COMPLETE")) tone = "hm-tag-green";
  else if (upper.includes("PEND") || upper.includes("PROGRESS") || upper.includes("INIT")) tone = "hm-tag-gold";
  else if (upper.includes("FAIL") || upper.includes("REJECT") || upper.includes("CANCEL")) tone = "hm-tag-red";
  return <span className={"hm-tag " + tone}>{status}</span>;
}

// An order that already came back successful has nothing left to correct.
function isSuccessStatus(status) {
  if (status === undefined || status === null || status === "") return false;
  return String(status).toUpperCase().includes("SUCCESS");
}

function OrderItems({ order }) {
  const items = order != null && order.selectedProducts != null ? order.selectedProducts : [];
  return (
    <>
      <div className="hm-lines">
        {items.map((prd, i) => (
          <div className="hm-line" key={i}>
            <div className="hm-line-main">
              <div className="hm-line-name">{prd.name}</div>
              <div className="hm-line-meta">
                Qty {prd.qty}
                {prd.code ? " · " + prd.code : ""}
              </div>
            </div>
            <div className="hm-line-amt">&#x20B9;{formatINR(prd.sellPrice)}</div>
          </div>
        ))}
      </div>
      <div className="hm-totals">
        <div className="hm-total-row is-grand">
          <span>Order total</span>
          <span>&#x20B9;{formatINR(order.orderTotal)}</span>
        </div>
      </div>
    </>
  );
}

function AdminPage() {
  const [modalLarge, setModalLarge] = React.useState(false);
  const [orders, setOrders] = React.useState(null);
  const [successOrders, setSuccessOrders] = React.useState(null);
  const [searchOrders, setSearchOrders] = React.useState(null);
  const history = useNavigate();
  const [token, setToken] = React.useState(null);
  const [viewOnly, setViewOnly] = React.useState(false);
  const [plainTabs, setPlainTabs] = React.useState(TAB_PASSES);
  const [filteredOrder, setFilteredOrder] = React.useState(null);
  const [filteredOrderN, setFilteredOrderN] = React.useState(null);
  const [filteredOrderS, setFilteredOrderS] = React.useState(null);
  const [modalLargeN, setModalLargeN] = React.useState(false);
  const [modalLargeS, setModalLargeS] = React.useState(false);
  const [mobileOrEmail, setMobileOrEmail] = useState(null);
  // Blocking is driven entirely from the products table's row menu.
  const [blockPrd, setBlockPrd] = useState(null);
  const [releasePrd, setReleasePrd] = useState(null);
  const [blockPrdName, setBlockPrdName] = useState("");
  const [blockPrdContact, setBlockPrdContact] = useState("");
  const [searching, setSearching] = useState(false);
  const [orderTSCursorS, setOrderTSCursorS] = React.useState(TS_CURSOR_START);
  const [allProducts, setAllProducts] = useState([]);
  const [membership, setMembership] = useState(false);
  const [sponsorship, setSponsorship] = useState(false);
  const [dailyMembership, setDailyPass] = useState(false);
  const [coSponsorPrice, setCoPrice] = useState(null);
  const [price, setPrice] = useState(null);
  const [name, setName] = useState(null);
  const [code, setCode] = useState(null);
  const [correctOrderId, setCorrectOrderId] = useState("");
  const [correctPayId, setCorrectPayId] = useState("");
  const [modalCorrect, setModalCorrect] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [fetchedOrder, setFetchedOrder] = useState(null);
  const [fetchingOrder, setFetchingOrder] = useState(false);
  const [fetchOrderError, setFetchOrderError] = useState(null);

  React.useEffect(() => {
    let userInfo = localStorage.getItem('user-info');
    if (userInfo !== null) {
      userInfo = JSON.parse(userInfo);
      if (userInfo.session != null) {
        if (!userInfo.admin) {
          history("/login");
          return;
        }
        setViewOnly(userInfo.viewOnly === true);
        setToken(userInfo.session);
        loadPendingOrderDetails(userInfo.session);
        loadSuccessOrderDetails(userInfo.session);
      } else {
        history("/login")
        return;
      }
    } else {
      history("/login");
      return;
    }
    document.body.classList.add("profile-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;

    return function cleanup() {
      document.body.classList.remove("profile-page");
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);

  async function loadPendingOrderDetails(token) {
    APIRequest(
      token,
      "/getPendingOrders",
      {},
      (res) => {
        setOrders(res.data);
      }
    );
  }

  async function loadSuccessOrderDetails(token) {
    APIRequest(
      token,
      "/getSuccessOrders",
      { orderTSCursor: orderTSCursorS },
      (res) => {
        if (orderTSCursorS === TS_CURSOR_START) {
          setSuccessOrders(res.data);
        } else {
          setSuccessOrders([...successOrders, ...res.data]);
        }
        if (res.data !== null) {
          let min = orderTSCursorS;
          res.data.map(s => {
            if (min > s.orderSuccessTS) {
              min = s.orderSuccessTS;
            }
          });
          setOrderTSCursorS(min);
        }
      }
    );
  }

  const loadSearchOrderDetails = () => {
    if (mobileOrEmail === null || String(mobileOrEmail).trim() === "") {
      toast.error("Please enter a mobile number or email to search");
      return;
    }
    setSearching(true);
    APIRequest(
      token,
      "/searchUserOrder",
      { emailId: mobileOrEmail, mobileNo: mobileOrEmail },
      (res) => {
        setSearching(false);
        setSearchOrders(res.data);
      },
      (err) => {
        setSearching(false);
        setSearchOrders([]);
        try {
          toast.error(err.data.desc);
        } catch (error) {
          toast.error("Could not search right now, please retry.");
        }
      },
      { busy: "Searching contributions…" }
    );
  }

  const handleMobileOrEmailChange = (e) => {
    setMobileOrEmail(e.target.value);
  };

  const handleOrderFetch = (orderUserId, orderRcptId) => {
    if (orders === null) return;
    let filteredOrder = orders.filter((order) => order.orderRcptId === orderRcptId && order.offlineUserId === orderUserId);
    setFilteredOrder(filteredOrder);
    setModalLarge(true);
  }

  const handleSuccessOrderFetch = (orderUserId, orderRcptId) => {
    if (successOrders === null) return;
    let filteredOrder = successOrders.filter((order) => order.orderRcptId === orderRcptId && order.offlineUserId === orderUserId);
    setFilteredOrderN(filteredOrder);
    setModalLargeN(true);
  }

  const handleSearchOrderFetch = (orderUserId, orderRcptId) => {
    if (searchOrders === null) return;
    let filteredOrder = searchOrders.filter((order) => order.orderRcptId === orderRcptId && order.offlineUserId === orderUserId);
    setFilteredOrderS(filteredOrder);
    setModalLargeS(true);
  }


  /**
   * Every write handler below starts with this. The controls are disabled for a view-only admin
   * already, so reaching one means a path we missed — a keyboard activation, a stale render. The
   * server refuses these regardless; this only saves the round trip and says why.
   */
  const blockedForViewOnly = () => {
    if (!viewOnly) return false;
    toast.error("You have view only access and cannot perform this operation!");
    return true;
  };

  const handleOfflineApprove = (offlineUserId, orderPkId) => {
    if (blockedForViewOnly()) return;
    APIRequest(
      token,
      "/approveOfflineOrder",
      { offlineUserId, orderPkId },
      (res) => {
        toast.success("You have approved the contribution successfully!");
        setModalLarge(false);
        loadPendingOrderDetails(token);
        loadSuccessOrderDetails(token);
      },
      null,
      { busy: "Approving the contribution…" }
    );
  }

  const handleMarkCouponCollected = (orderRcptId) => {
    if (blockedForViewOnly()) return;
    APIRequest(
      token,
      "/setPassCollectionState",
      { orderRcptId, state: "COLLECTED" },
      (res) => {
        toast.success("Pass marked as collected!");
        setModalLargeS(false);
        setFilteredOrderS(null);
        setSearchOrders(null);
        loadPendingOrderDetails(token);
        loadSuccessOrderDetails(token);
      },
      null,
      { busy: "Marking the pass collected…" }
    );
  }

  const handleOfflineReject = (offlineUserId, orderPkId) => {
    if (blockedForViewOnly()) return;
    APIRequest(
      token,
      "/rejectOfflineOrder",
      { offlineUserId, orderPkId },
      (res) => {
        toast.success("You have rejected the contribution successfully!");
        setModalLarge(false);
        loadPendingOrderDetails(token);
      },
      null,
      { busy: "Rejecting the contribution…" }
    );
  }

  const handleProductBlock = (token, id, blockedByName, blockedByEmail, onDone) => {
    if (blockedForViewOnly()) return;
    if (id === null || id === "product") {
      toast.error("Please choose the sponsorship item to block");
      return;
    }
    if (blockedByName === null || String(blockedByName).trim() === ""
      || blockedByEmail === null || String(blockedByEmail).trim() === "") {
      toast.error("Sponsor name and contact are mandatory");
      return;
    }
    APIRequest(
      token,
      "/blockProduct",
      { id, blockedByName, blockedByEmail },
      (res) => {
        toast.success("Product blocked for: " + blockedByName + " (" + blockedByEmail + ")");
        loadAllProducts(token);
        if (onDone != null) onDone();
      },
      null,
      { busy: "Blocking the item…" }
    );
  }

  // Clearing a sponsorship used to mean two calls — publish it, then block it again with the
  // name fields empty — and skipping the second left the item on sale still crediting whoever
  // held it before. /unblockProduct does the whole release in one go.
  const handleProductUnblock = (token, id, onDone) => {
    if (blockedForViewOnly()) return;
    if (id === null || id === undefined) {
      toast.error("Please choose the sponsorship item to release");
      return;
    }
    APIRequest(
      token,
      "/unblockProduct",
      { id },
      (res) => {
        toast.success("Item released — it is back on sale and carries no sponsor");
        loadAllProducts(token);
        if (onDone != null) onDone();
      },
      null,
      { busy: "Releasing the item…" }
    );
  }

  function loadAllProducts(token) {
    APIRequest(
      token,
      "/getUnGropedProducts",
      {},
      (res) => {
        setAllProducts(res.data);
      }
    );
  }

  const handleMembershipChange = (event) => {
    if (event.target.value === "M") {
      setMembership(true);
      setSponsorship(false);
      setDailyPass(false);
    } else if (event.target.value === "S") {
      setMembership(false);
      setSponsorship(true);
      setDailyPass(false);
    } if (event.target.value === "D") {
      setMembership(false);
      setSponsorship(false);
      setDailyPass(true);
    }
  }

  const addProduct = (token) => {
    if (blockedForViewOnly()) return;
    if (!membership && !sponsorship && !dailyMembership) {
      toast.error("Please choose a product type first");
      return;
    }
    if (name === null || String(name).trim() === "" || code === null || String(code).trim() === "") {
      toast.error("Product name and code are mandatory");
      return;
    }
    if (price === null || String(price).trim() === "") {
      toast.error("Please add a price");
      return;
    }
    APIRequest(
      token,
      "/addProduct",
      { code, name, price, coSponsorPrice, membership, sponsorship, dailyMembership },
      (res) => {
        loadAllProducts(token);
        toast.success("Product added successfully!");
      },
      null,
      { busy: "Adding the product…" }
    );
  }

  const markProductAvailability = (token, id, available) => {
    if (blockedForViewOnly()) return;
    APIRequest(
      token,
      "/markProductAvailable",
      { id, available },
      (res) => {
        loadAllProducts(token);
        toast.success("Product availbility updated!");
      },
      null,
      { busy: "Updating availability…" }
    );
  }

  const openBlockProduct = (product) => {
    setBlockPrd(product);
    setBlockPrdName("");
    setBlockPrdContact("");
  }

  const closeBlockProduct = () => {
    setBlockPrd(null);
    setBlockPrdName("");
    setBlockPrdContact("");
  }

  const openReleaseProduct = (product) => {
    setReleasePrd(product);
  }

  const closeReleaseProduct = () => {
    setReleasePrd(null);
  }

  const isSponsorshipProduct = (product) =>
    product != null && (product.type === "SPONSORSHIP" || product.sponsorship === true);

  const sponsorNameOf = (product) =>
    product != null
      && product.sponsoredBy !== undefined
      && product.sponsoredBy !== null
      && String(product.sponsoredBy).trim() !== ""
      ? product.sponsoredBy
      : null;

  // A sponsorship is "held" once it carries a sponsor — whether it was reserved by hand for
  // someone who paid offline, or settled automatically against a payment. Both read as
  // unavailable here, because getUnGropedProducts resolves availability with no viewer email.
  const isProductHeld = (product) =>
    isSponsorshipProduct(product) && sponsorNameOf(product) !== null;

  // Actions shown behind the row's ⋮ menu on the products table.
  const productActions = (product) => {
    const actions = [];
    const held = isProductHeld(product);
    if (product.available === true) {
      actions.push({
        key: "hide",
        label: "Hide from public pages",
        icon: "now-ui-icons media-1_button-power",
        disabled: viewOnly,
        onSelect: () => markProductAvailability(token, product.id, false),
      });
    } else if (!held) {
      // Publish is deliberately not offered for a held item. On a reserved one it does nothing
      // visible — the availability bit is already set and the row still reads as unavailable
      // because of the sponsor — and on a settled one it puts the item back on sale while the
      // previous sponsor's name is still attached. Release is the action that means either.
      actions.push({
        key: "publish",
        label: "Publish",
        icon: "now-ui-icons ui-1_check",
        disabled: viewOnly,
        onSelect: () => markProductAvailability(token, product.id, true),
      });
    }
    if (isSponsorshipProduct(product)) {
      actions.push({
        key: "cosponsor",
        label: product.fullSponsorOnly ? "Enable co-sponsor" : "Disable co-sponsor",
        icon: "now-ui-icons users_circle-08",
        disabled: viewOnly,
        onSelect: () => markProductOnlyFullSponsor(token, product.id, !product.fullSponsorOnly),
      });
      // Blocking by hand lives here only — the row's own menu, where the item is already
      // picked. There is no separate Block product tab any more.
      actions.push({
        key: "block",
        label: held ? "Reassign sponsor" : "Block product",
        icon: "now-ui-icons ui-1_lock-circle-open",
        tone: held ? undefined : "danger",
        disabled: viewOnly,
        onSelect: () => openBlockProduct(product),
      });
      if (held) {
        actions.push({
          key: "release",
          label: "Release sponsorship",
          icon: "now-ui-icons ui-1_simple-remove",
          tone: "danger",
          disabled: viewOnly,
          onSelect: () => openReleaseProduct(product),
        });
      }
    }
    return actions;
  }

  const markProductOnlyFullSponsor = (token, id, fullSponsorOnly) => {
    if (blockedForViewOnly()) return;
    APIRequest(
      token,
      "/markProductFullSponsor",
      { id, fullSponsorOnly },
      (res) => {
        loadAllProducts(token);
        toast.success("Product spnsorship status updated!");
      },
      null,
      { busy: "Updating sponsorship status…" }
    );
  }

  // Reconciles a payment that succeeded at Razorpay but never landed back on
  // the order — the backend re-signs it and replays the success flow.
  // Pulls the order and its contributor so the admin can confirm who they are
  // about to settle before anything is written.
  const loadOrderForCorrection = () => {
    if (String(correctOrderId).trim() === "") {
      toast.error("Please enter the Razorpay order ID to look up");
      return;
    }
    setFetchingOrder(true);
    setFetchedOrder(null);
    setFetchOrderError(null);
    APIRequest(
      token,
      "/getOrderByOrderId",
      { orderId: String(correctOrderId).trim() },
      (res) => {
        setFetchingOrder(false);
        if (res.data === null || res.data === undefined || res.data.orderRcptId === undefined) {
          setFetchedOrder(null);
          setFetchOrderError("No order exists against that Razorpay order ID.");
          return;
        }
        setFetchedOrder(res.data);
      },
      (err) => {
        setFetchingOrder(false);
        setFetchedOrder(null);
        let message = "Could not find an order against that Razorpay order ID.";
        try {
          if (err.data.desc) message = err.data.desc;
        } catch (error) {
          // fall through to the default message
        }
        setFetchOrderError(message);
        toast.error(message);
      },
      { busy: "Fetching the order…" }
    );
  }

  const openCorrectOrderConfirm = () => {
    if (String(correctOrderId).trim() === "" || String(correctPayId).trim() === "") {
      toast.error("Both the Razorpay order ID and payment ID are mandatory");
      return;
    }
    if (fetchedOrder === null) {
      toast.error(fetchOrderError !== null
        ? fetchOrderError
        : "Fetch the order first so you can verify it before correcting");
      return;
    }
    setModalCorrect(true);
  }

  const handleCorrectOrder = () => {
    if (blockedForViewOnly()) return;
    setCorrecting(true);
    APIRequest(
      token,
      "/correctOrder",
      { orderId: String(correctOrderId).trim(), payId: String(correctPayId).trim() },
      (res) => {
        setCorrecting(false);
        setModalCorrect(false);
        setCorrectOrderId("");
        setCorrectPayId("");
        setFetchedOrder(null);
        toast.success("Order corrected and marked successful!");
        loadPendingOrderDetails(token);
        loadSuccessOrderDetails(token);
      },
      (err) => {
        setCorrecting(false);
        setModalCorrect(false);
        try {
          toast.error(err.data.desc);
        } catch (error) {
          toast.error("Could not correct this order, please recheck the IDs.");
        }
      },
      { busy: "Correcting the order…" }
    );
  }

  const handleTabSelect = (id) => {
    if (id === TAB_PRODUCTS) {
      loadAllProducts(token);
    }
    setPlainTabs(id);
  }

  const pendingCount = orders !== null ? orders.length : null;
  const successCount = successOrders !== null ? successOrders.length : null;

  return (
    <>
      <IndexNavbar />
      <div className="wrapper">
        <div className="main hm hm-adm">
          <header className="hm-adm-head">
            <div className="hm-wrap">
              <span className="hm-adm-eyebrow">SORRBA</span>
              <h1>Admin console</h1>
              <p>Verify contributions, hand out passes and manage what goes on sale.</p>
            </div>
          </header>

          {viewOnly && (
            <div className="hm-wrap">
              <div className="hm-adm-viewonly" role="status">
                <i className="now-ui-icons ui-1_lock-circle-open" aria-hidden="true"></i>
                <span>
                  <strong>View-only access.</strong> You can see everything here, but approvals,
                  corrections and product changes are turned off for this account.
                </span>
              </div>
            </div>
          )}

          <div className="hm-wrap hm-adm-layout">
            <nav className="hm-adm-rail" aria-label="Admin sections">
              <div className="hm-adm-rail-inner">
                {TAB_GROUPS.map((group) => (
                  <React.Fragment key={group.label}>
                    <span className="hm-adm-rail-grp">{group.label}</span>
                    {group.tabs.map((tab) => (
                      <button
                        className={"hm-adm-tab" + (plainTabs === tab.id ? " is-on" : "")}
                        type="button"
                        key={tab.id}
                        aria-current={plainTabs === tab.id ? "page" : undefined}
                        onClick={() => handleTabSelect(tab.id)}
                      >
                        {tab.label}
                        {tab.id === TAB_PENDING && pendingCount > 0 && (
                          <span className="hm-adm-tab-n">{pendingCount}</span>
                        )}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </nav>

            <section className="hm-adm-body">

              {plainTabs === TAB_PASSES && (
                <PassCollection token={token} viewOnly={viewOnly} />
              )}

              {plainTabs === TAB_FIND_ORDER && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>Pass collection</h2>
                      <p>Look up a contributor by mobile or email, then mark their pass as collected.</p>
                    </div>
                  </div>
                  <div className="hm-adm-search">
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-search">Mobile or email</label>
                      <div className="hm-fld-wrap">
                        <input
                          id="adm-search"
                          className="hm-fld-i"
                          type="text"
                          placeholder="9876543210 or name@example.com"
                          onChange={handleMobileOrEmailChange}
                          onKeyDown={(e) => { if (e.key === "Enter") loadSearchOrderDetails() }}
                        />
                      </div>
                    </div>
                    <button
                      className="hm-btn hm-btn-primary"
                      type="button"
                      onClick={() => loadSearchOrderDetails()}
                      disabled={searching}
                    >
                      {searching ? "Searching…" : "Search orders"}
                    </button>
                  </div>

                  {searchOrders !== null && searchOrders.length > 0 && (
                    <div className="hm-tbl-wrap" style={{ marginTop: "1.4rem" }}>
                      <table className="hm-tbl">
                        <thead>
                          <tr>
                            <th>Contribution ID</th>
                            <th className="is-num">Amount</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            <th className="is-act">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchOrders.map((orderS, i) => (
                            <tr key={orderS.orderRcptId != null ? orderS.orderRcptId : i}>
                              <td className="is-id">{orderS.orderRcptId}</td>
                              <td className="is-num is-amt">&#x20B9;{formatINR(orderS.orderTotal)}</td>
                              <td className="is-name">{orderS.userName}</td>
                              <td className="is-id">{orderS.userMobile}</td>
                              <td className="is-act">
                                {orderS.passCollected !== null && orderS.passCollected === 0 && (
                                  <button
                                    className="hm-btn hm-btn-outline hm-btn-sm"
                                    type="button"
                                    onClick={() => handleSearchOrderFetch(orderS.offlineUserId, orderS.orderRcptId)}
                                  >
                                    View details
                                  </button>
                                )}
                                {orderS.passCollected !== null && orderS.passCollected === 1 && (
                                  <span className="hm-tag hm-tag-green">Pass collected</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {searchOrders !== null && searchOrders.length === 0 && (
                    <TableState
                      icon="now-ui-icons ui-1_zoom-bold"
                      title="No orders found"
                      message="Nothing matched that mobile number or email. Try the other one, or check for typos."
                    />
                  )}
                </div>
              )}

              {plainTabs === TAB_PENDING && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>Pending orders</h2>
                      <p>Direct transfers waiting for your verification.</p>
                    </div>
                    <div className="hm-adm-tools">
                      <button
                        className="hm-btn hm-btn-outline hm-btn-sm"
                        type="button"
                        onClick={() => loadPendingOrderDetails(token)}
                      >
                        <i className="now-ui-icons arrows-1_refresh-69"></i> Refresh
                      </button>
                    </div>
                  </div>

                  {orders !== null && orders.length > 0 && (
                    <div className="hm-tbl-wrap">
                      <table className="hm-tbl">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Contribution ID</th>
                            <th>Transaction ID</th>
                            <th className="is-num">Amount</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            <th>Status</th>
                            <th className="is-act">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order, i) => (
                            <tr key={order.orderRcptId != null ? order.orderRcptId : i}>
                              <td>{order.fiscalYear}</td>
                              <td className="is-id">{order.orderRcptId}</td>
                              <td className="is-id">{order.transactionId}</td>
                              <td className="is-num is-amt">&#x20B9;{formatINR(order.orderTotal)}</td>
                              <td className="is-name">{order.userName}</td>
                              <td className="is-id">{order.userMobile}</td>
                              <td><StatusTag status={order.status} /></td>
                              <td className="is-act">
                                <button
                                  className="hm-btn hm-btn-outline hm-btn-sm"
                                  type="button"
                                  onClick={() => handleOrderFetch(order.offlineUserId, order.orderRcptId)}
                                >
                                  Review
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(orders === null || orders.length <= 0) && (
                    <TableState
                      icon="now-ui-icons ui-1_check"
                      title="All clear"
                      message="No contribution is pending verification right now."
                    />
                  )}
                </div>
              )}

              {plainTabs === TAB_SUCCESS && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>Successful orders</h2>
                      <p>Verified and paid contributions, newest first.</p>
                    </div>
                    {successCount !== null && (
                      <div className="hm-adm-tools">
                        <span className="hm-tag hm-tag-indigo">{successCount} loaded</span>
                      </div>
                    )}
                  </div>

                  {successOrders !== null && successOrders.length > 0 && (
                    <>
                      <div className="hm-tbl-wrap">
                        <table className="hm-tbl">
                          <thead>
                            <tr>
                              <th>Year</th>
                              <th>Date</th>
                              <th>Contribution ID</th>
                              <th>Transaction ID</th>
                              <th className="is-num">Amount</th>
                              <th>Name</th>
                              <th>Mobile</th>
                              <th className="is-act">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {successOrders.map((order, i) => (
                              <tr key={(order.orderRcptId != null ? order.orderRcptId : "o") + "-" + i}>
                                <td>{order.fiscalYear}</td>
                                <td>{order.orderDate}</td>
                                <td className="is-id">{order.orderRcptId}</td>
                                <td className="is-id">{order.transactionId}</td>
                                <td className="is-num is-amt">&#x20B9;{formatINR(order.orderTotal)}</td>
                                <td className="is-name">{order.userName}</td>
                                <td className="is-id">{order.userMobile}</td>
                                <td className="is-act">
                                  <button
                                    className="hm-btn hm-btn-outline hm-btn-sm"
                                    type="button"
                                    onClick={() => handleSuccessOrderFetch(order.offlineUserId, order.orderRcptId)}
                                  >
                                    View details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="hm-tbl-foot">
                        <button
                          className="hm-btn hm-btn-outline"
                          type="button"
                          onClick={() => loadSuccessOrderDetails(token)}
                        >
                          Load more
                        </button>
                      </div>
                    </>
                  )}

                  {(successOrders === null || successOrders.length <= 0) && (
                    <TableState
                      icon="now-ui-icons files_paper"
                      title="Nothing here yet"
                      message="No successful contribution has been recorded so far."
                    />
                  )}
                </div>
              )}

              {plainTabs === TAB_ADD_PRODUCT && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>Add a product</h2>
                      <p>Create a membership tier, sponsorship item or daily pass.</p>
                    </div>
                  </div>
                  <div className="hm-fgrid">
                    <div className="hm-fld hm-fld-full">
                      <label className="hm-fld-l" htmlFor="adm-type">Product type *</label>
                      <div className="hm-fld-wrap hm-fld-sel">
                        <select id="adm-type" className="hm-fld-s" onChange={handleMembershipChange}>
                          <option value="X">Choose…</option>
                          <option value="M">Membership</option>
                          <option value="S">Sponsorship</option>
                          <option value="D">Daily pass</option>
                        </select>
                      </div>
                    </div>
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-name">Product name *</label>
                      <div className="hm-fld-wrap">
                        <input
                          id="adm-name"
                          className="hm-fld-i"
                          type="text"
                          placeholder="e.g. UTSAV"
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-code">Product code *</label>
                      <div className="hm-fld-wrap">
                        <input
                          id="adm-code"
                          className="hm-fld-i"
                          type="text"
                          placeholder="4-character unique code"
                          onChange={(e) => setCode(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-price">Full price *</label>
                      <div className="hm-fld-wrap">
                        <span className="hm-fld-pre" aria-hidden="true">&#x20B9;</span>
                        <input
                          id="adm-price"
                          className="hm-fld-i"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          onChange={(e) => setPrice(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-coprice">Co-sponsor / Utsav price</label>
                      <div className="hm-fld-wrap">
                        <span className="hm-fld-pre" aria-hidden="true">&#x20B9;</span>
                        <input
                          id="adm-coprice"
                          className="hm-fld-i"
                          type="text"
                          inputMode="numeric"
                          placeholder="Leave blank for full-sponsor only"
                          onChange={(e) => setCoPrice(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="hm-tbl-foot">
                    <button className="hm-btn hm-btn-primary" type="button" disabled={viewOnly} onClick={() => addProduct(token)}>
                      Add product
                    </button>
                  </div>
                </div>
              )}

              {plainTabs === TAB_PRODUCTS && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>All products</h2>
                      <p>Toggle what appears on the public pages.</p>
                    </div>
                    <div className="hm-adm-tools">
                      <button
                        className="hm-btn hm-btn-outline hm-btn-sm"
                        type="button"
                        onClick={() => loadAllProducts(token)}
                      >
                        <i className="now-ui-icons arrows-1_refresh-69"></i> Refresh
                      </button>
                    </div>
                  </div>

                  {allProducts !== null && allProducts.length > 0 && (
                    <div className="hm-tbl-wrap">
                      <table className="hm-tbl">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Name</th>
                            <th>Code</th>
                            <th className="is-num">Full price</th>
                            <th className="is-num">Co / Utsav</th>
                            <th>On UI</th>
                            <th>Full sponsor only</th>
                            <th>Sponsored / blocked by</th>
                            <th className="is-act is-stuck">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allProducts.map((product, i) => (
                            <tr key={product.id != null ? product.id : i}>
                              <td>
                                <span className={"hm-tag " + (TYPE_TONES[product.type] || "hm-tag-mute")}>
                                  {product.type}
                                </span>
                              </td>
                              <td className="is-name">{product.name}</td>
                              <td className="is-id">{product.code}</td>
                              <td className="is-num">&#x20B9;{formatINR(product.price)}</td>
                              <td className="is-num">
                                {product.coPrice != null ? "₹" + formatINR(product.coPrice) : "—"}
                              </td>
                              <td>
                                {product.available
                                  ? <span className="hm-tag hm-tag-green">Live</span>
                                  : <span className="hm-tag hm-tag-mute">Hidden</span>}
                              </td>
                              <td>
                                {product.type === "SPONSORSHIP"
                                  ? product.fullSponsorOnly
                                    ? <span className="hm-tag hm-tag-gold">Yes</span>
                                    : <span className="hm-tag hm-tag-mute">No</span>
                                  : <span className="hm-tag hm-tag-mute">N/A</span>}
                              </td>
                              <td>
                                {product.sponsoredBy !== undefined && product.sponsoredBy !== null
                                  ? product.sponsoredBy +
                                  (product.sponsoredByEmail !== undefined && product.sponsoredByEmail !== null
                                    ? " · " + product.sponsoredByEmail
                                    : "")
                                  : "—"}
                              </td>
                              <td className="is-act is-stuck">
                                <RowMenu
                                  label={"Actions for " + product.name}
                                  items={productActions(product)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(allProducts === null || allProducts.length === 0) && (
                    <TableState
                      icon="now-ui-icons shopping_tag-content"
                      title="No products loaded"
                      message="Nothing came back from the products list."
                    />
                  )}
                </div>
              )}

              {plainTabs === TAB_CORRECT_ORDER && (
                <div className="hm-panel">
                  <div className="hm-adm-panel-head">
                    <div>
                      <h2>Correct an order</h2>
                      <p>Settle an order whose payment went through at Razorpay but never made it back to us.</p>
                    </div>
                  </div>

                  <div className="hm-adm-note">
                    <i className="now-ui-icons ui-1_bell-53" aria-hidden="true"></i>
                    <p>
                      Use this only when you have confirmed the payment on the{" "}
                      <a
                        href={RAZORPAY_DASHBOARD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <strong>Razorpay dashboard</strong>
                      </a>. It marks the order successful and sends the contributor
                      their receipt — there is no undo.
                    </p>
                  </div>

                  <div className="hm-adm-search">
                    <div className="hm-fld">
                      <label className="hm-fld-l" htmlFor="adm-corr-order">Razorpay order ID *</label>
                      <div className="hm-fld-wrap">
                        <input
                          id="adm-corr-order"
                          className="hm-fld-i"
                          type="text"
                          placeholder="order_XXXXXXXXXXXXXX"
                          value={correctOrderId}
                          onChange={(e) => {
                            setCorrectOrderId(e.target.value);
                            setFetchedOrder(null);
                            setFetchOrderError(null);
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") loadOrderForCorrection() }}
                        />
                      </div>
                    </div>
                    <button
                      className="hm-btn hm-btn-primary"
                      type="button"
                      onClick={() => loadOrderForCorrection()}
                      disabled={fetchingOrder}
                    >
                      {fetchingOrder ? "Fetching…" : "Fetch order"}
                    </button>
                  </div>

                  {fetchedOrder !== null && (
                    <div style={{ marginTop: "1.6rem" }}>
                      <div className="hm-adm-panel-head">
                        <div>
                          <h2>{fetchedOrder.userName}</h2>
                          <p>Confirm this is the right contribution before settling it.</p>
                        </div>
                        <div className="hm-adm-tools">
                          <StatusTag status={fetchedOrder.status} />
                        </div>
                      </div>

                      <OrderItems order={fetchedOrder} />
                      <MetaList
                        rows={[
                          ["Order date", fetchedOrder.orderDate],
                          ["Contribution ID", fetchedOrder.orderRcptId],
                          ["Razorpay order ID", fetchedOrder.orderId],
                          ["Payment type", fetchedOrder.paymentType],
                          ["Existing transaction ID", fetchedOrder.transactionId],
                          ["Contributor", fetchedOrder.userName],
                          ["Email", fetchedOrder.userEmail],
                          ["Mobile", fetchedOrder.userMobile],
                          ["Fiscal year", fetchedOrder.fiscalYear],
                        ]}
                      />

                      {isSuccessStatus(fetchedOrder.status) ? (
                        <div className="hm-adm-note" style={{ marginTop: "1.6rem" }}>
                          <i className="now-ui-icons ui-1_check" aria-hidden="true"></i>
                          <p>
                            This order is already marked successful — there is
                            nothing to correct.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="hm-fgrid" style={{ marginTop: "1.6rem" }}>
                            <div className="hm-fld">
                              <label className="hm-fld-l" htmlFor="adm-corr-pay">Razorpay payment ID *</label>
                              <div className="hm-fld-wrap">
                                <input
                                  id="adm-corr-pay"
                                  className="hm-fld-i"
                                  type="text"
                                  placeholder="pay_XXXXXXXXXXXXXX"
                                  value={correctPayId}
                                  onChange={(e) => setCorrectPayId(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") openCorrectOrderConfirm() }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="hm-tbl-foot">
                            <button
                              className="hm-btn hm-btn-primary"
                              type="button"
                              onClick={() => openCorrectOrderConfirm()}
                              disabled={correcting || viewOnly}
                            >
                              {correcting ? "Correcting…" : "Correct order"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {fetchedOrder === null && !fetchingOrder && fetchOrderError !== null && (
                    <TableState
                      icon="now-ui-icons ui-1_simple-remove"
                      title="Order not found"
                      message={fetchOrderError}
                    />
                  )}

                  {fetchedOrder === null && !fetchingOrder && fetchOrderError === null && (
                    <TableState
                      icon="now-ui-icons ui-1_zoom-bold"
                      title="Fetch an order first"
                      message="Enter the Razorpay order ID above to pull up the contribution and who placed it."
                    />
                  )}
                </div>
              )}

            </section>
          </div>
        </div>
        <DefaultFooter />
      </div>

      <Modal isOpen={modalLargeS} size="lg" centered scrollable contentClassName="hm-modal">
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">Pass collection</span>
            <h5 className="hm-modal-title">Contribution details</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={() => setModalLargeS(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          {filteredOrderS !== null && filteredOrderS[0] && (
            <>
              <OrderItems order={filteredOrderS[0]} />
              <MetaList
                rows={[
                  ["Order date", filteredOrderS[0].orderDate],
                  ["Contribution ID", filteredOrderS[0].orderRcptId],
                  ["Contributor", filteredOrderS[0].userName],
                  ["Email", filteredOrderS[0].userEmail],
                  ["Mobile", filteredOrderS[0].userMobile],
                ]}
              />
            </>
          )}
        </div>
        {filteredOrderS !== null && filteredOrderS[0] &&
          (filteredOrderS[0].passCollected === null || filteredOrderS[0].passCollected === 0) && (
            <div className="hm-modal-foot">
              <button
                className="hm-btn hm-btn-primary"
                type="button"
                disabled={viewOnly}
                onClick={() => handleMarkCouponCollected(filteredOrderS[0].orderRcptId)}
              >
                Mark pass as collected
              </button>
            </div>
          )}
      </Modal>

      <Modal
        isOpen={modalLarge}
        size="lg"
        centered
        scrollable
        contentClassName="hm-modal"
        toggle={() => setModalLarge(false)}
      >
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">Awaiting verification</span>
            <h5 className="hm-modal-title">Pending contribution</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={() => setModalLarge(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          {filteredOrder !== null && filteredOrder[0] && (
            <>
              <OrderItems order={filteredOrder[0]} />
              <MetaList
                rows={[
                  ["Contribution ID", filteredOrder[0].orderRcptId],
                  ["Status", filteredOrder[0].status],
                  ["Transaction ID", filteredOrder[0].transactionId],
                  ["Bank", filteredOrder[0].bankName],
                  ["Contributor", filteredOrder[0].userName],
                  ["Email", filteredOrder[0].userEmail],
                  ["Mobile", filteredOrder[0].userMobile],
                  ["Refer source", filteredOrder[0].referSource],
                  ["Referred by", filteredOrder[0].referredBy],
                  ["Address", [filteredOrder[0].addr1, filteredOrder[0].addr2].filter(Boolean).join(", ")],
                  ["City", filteredOrder[0].city],
                ]}
              />
            </>
          )}
        </div>
        {filteredOrder !== null && filteredOrder[0] && (
          <div className="hm-modal-foot">
            <button
              className="hm-btn hm-btn-outline"
              type="button"
              disabled={viewOnly}
              onClick={() => handleOfflineReject(filteredOrder[0].offlineUserId, filteredOrder[0].orderPkId)}
            >
              Reject
            </button>
            <button
              className="hm-btn hm-btn-primary"
              type="button"
              disabled={viewOnly}
              onClick={() => handleOfflineApprove(filteredOrder[0].offlineUserId, filteredOrder[0].orderPkId)}
            >
              Approve contribution
            </button>
          </div>
        )}
      </Modal>

      <Modal isOpen={modalLargeN} size="lg" centered scrollable contentClassName="hm-modal">
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">Verified</span>
            <h5 className="hm-modal-title">Contribution details</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={() => setModalLargeN(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          {filteredOrderN !== null && filteredOrderN[0] && (
            <>
              <OrderItems order={filteredOrderN[0]} />
              <MetaList
                rows={[
                  ["Order date", filteredOrderN[0].orderDate],
                  ["Contribution ID", filteredOrderN[0].orderRcptId],
                  ["Status", filteredOrderN[0].status],
                  ["Payment type", filteredOrderN[0].paymentType],
                  ["Transaction ID", filteredOrderN[0].transactionId],
                  ["Bank", filteredOrderN[0].bankName],
                  ["Contributor", filteredOrderN[0].userName],
                  ["Email", filteredOrderN[0].userEmail],
                  ["Mobile", filteredOrderN[0].userMobile],
                  ["Refer source", filteredOrderN[0].referSource],
                  ["Referred by", filteredOrderN[0].referredBy],
                  ["Address", [filteredOrderN[0].addr1, filteredOrderN[0].addr2].filter(Boolean).join(", ")],
                  ["City", filteredOrderN[0].city],
                ]}
              />
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={modalCorrect}
        size="md"
        centered
        contentClassName="hm-modal"
        toggle={() => setModalCorrect(false)}
      >
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">Confirm correction</span>
            <h5 className="hm-modal-title">Mark this order successful?</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={() => setModalCorrect(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          <p style={{ margin: 0 }}>
            The order will be settled against this payment and the contributor will be
            notified. This cannot be reversed from the console.
          </p>
          <MetaList
            rows={[
              ["Contributor", fetchedOrder !== null ? fetchedOrder.userName : null],
              ["Mobile", fetchedOrder !== null ? fetchedOrder.userMobile : null],
              ["Contribution ID", fetchedOrder !== null ? fetchedOrder.orderRcptId : null],
              ["Amount", fetchedOrder !== null ? "₹" + formatINR(fetchedOrder.orderTotal) : null],
              ["Razorpay order ID", correctOrderId],
              ["Razorpay payment ID", correctPayId],
            ]}
          />
        </div>
        <div className="hm-modal-foot">
          <button
            className="hm-btn hm-btn-outline"
            type="button"
            onClick={() => setModalCorrect(false)}
          >
            Cancel
          </button>
          <button
            className="hm-btn hm-btn-primary"
            type="button"
            onClick={() => handleCorrectOrder()}
            disabled={correcting || viewOnly}
          >
            {correcting ? "Correcting…" : "Yes, correct it"}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={releasePrd !== null}
        size="md"
        centered
        contentClassName="hm-modal"
        toggle={closeReleaseProduct}
      >
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">Release sponsorship</span>
            <h5 className="hm-modal-title">{releasePrd !== null ? releasePrd.name : ""}</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={closeReleaseProduct}
          >
            <span aria-hidden={true}>&times;</span>
          </button>
        </div>
        <div className="hm-modal-body">
          <p style={{ marginTop: 0 }}>
            This puts the item back on sale and removes the sponsor's name from the public
            page. Anyone will be able to sponsor it again.
          </p>
          <div className="hm-adm-note">
            <i className="now-ui-icons ui-1_bell-53" aria-hidden="true"></i>
            <p>
              If this item was paid for, releasing it does <strong>not</strong> refund or cancel
              the contribution — the order stays as it is. Only do this when the item genuinely
              needs to go back on sale.
            </p>
          </div>
          <MetaList
            rows={[
              ["Item", releasePrd !== null ? releasePrd.name : null],
              ["Code", releasePrd !== null ? releasePrd.code : null],
              ["Currently against", releasePrd !== null ? sponsorNameOf(releasePrd) : null],
              ["Contact", releasePrd !== null ? releasePrd.sponsoredByEmail : null],
            ]}
          />
        </div>
        <div className="hm-modal-foot">
          <button className="hm-btn hm-btn-outline" type="button" onClick={closeReleaseProduct}>
            Cancel
          </button>
          <button
            className="hm-btn hm-btn-danger"
            type="button"
            disabled={viewOnly}
            onClick={() => handleProductUnblock(
              token,
              releasePrd !== null ? releasePrd.id : null,
              closeReleaseProduct
            )}
          >
            Yes, release it
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={blockPrd !== null}
        size="md"
        centered
        contentClassName="hm-modal"
        toggle={closeBlockProduct}
      >
        <div className="hm-modal-head">
          <div>
            <span className="hm-modal-eyebrow">
              {isProductHeld(blockPrd) ? "Reassign sponsor" : "Block product"}
            </span>
            <h5 className="hm-modal-title">{blockPrd !== null ? blockPrd.name : ""}</h5>
          </div>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={closeBlockProduct}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          <p style={{ marginTop: 0 }}>
            Reserve this item for someone who contributed outside the site. It stops
            showing as available and carries their name on the public page.
          </p>
          {blockPrd !== null
            && blockPrd.sponsoredBy !== undefined
            && blockPrd.sponsoredBy !== null
            && blockPrd.sponsoredBy !== "" && (
              <div className="hm-adm-note">
                <i className="now-ui-icons ui-1_bell-53" aria-hidden="true"></i>
                <p>
                  This item is already against <strong>{blockPrd.sponsoredBy}</strong>.
                  Blocking it again will overwrite that.
                </p>
              </div>
            )}
          <div className="hm-fld">
            <label className="hm-fld-l" htmlFor="adm-row-blk-name">Sponsor name *</label>
            <div className="hm-fld-wrap">
              <input
                id="adm-row-blk-name"
                className="hm-fld-i"
                type="text"
                placeholder="Name to display"
                value={blockPrdName}
                onChange={(e) => setBlockPrdName(e.target.value)}
              />
            </div>
          </div>
          <div className="hm-fld">
            <label className="hm-fld-l" htmlFor="adm-row-blk-contact">Mobile or email *</label>
            <div className="hm-fld-wrap">
              <input
                id="adm-row-blk-contact"
                className="hm-fld-i"
                type="text"
                placeholder="Contact of the sponsor"
                value={blockPrdContact}
                onChange={(e) => setBlockPrdContact(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="hm-modal-foot">
          <button className="hm-btn hm-btn-outline" type="button" onClick={closeBlockProduct}>
            Cancel
          </button>
          <button
            className="hm-btn hm-btn-danger"
            type="button"
            disabled={viewOnly}
            onClick={() => handleProductBlock(
              token,
              blockPrd !== null ? blockPrd.id : null,
              blockPrdName,
              blockPrdContact,
              closeBlockProduct
            )}
          >
            {isProductHeld(blockPrd) ? "Reassign sponsor" : "Block product"}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default AdminPage;
